'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Clients, storageTTL } from '../utils/constants';
import { AutosaveContext } from './contexts/AutosaveContext';
import {
  BusinessRules,
  DefaultValues,
  Fabric,
  HiddenOptionsAndValues,
  Monogram,
  OptionConfigurationType,
  OptionsSelection,
  SizePassportSelection,
  StylePreset,
  StylePresets,
  typeFabricList,
  typeOptionValue,
} from '../utils/interfaces';
import useFirstRender from '../components/sectionContainer/useFirstRender';
import {
  buildAutosaveCacheKey,
  deepEqual,
  findFabricDefaultSelection,
  getProductDefaultHiddenItemsState,
} from '../utils/utils';
import useFabricContext from './hooks/useFabricContext';
import useNavigationContext from './hooks/useNavigationContext';
import useSizePassportContext from './hooks/useSizePassportContext';
import useGlobalContext from './hooks/useGlobalContext';
import useUserContext from './hooks/useUserContext';
import useOptionContext from './hooks/useOptionContext';
import useMissingSelectionValuesContext from './hooks/useMissingSelectionValuesContext';
import { useEnvironmentContext } from './hooks/useEnvironmentContext';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { useEventCallback } from '../hooks/useEventCallback';
import { FEATURE_TOGGLES } from '../feature-flags/featureToggles';
import { Section } from '../utils/navigation';
import { AutosaveData, getAutosaveResumeDefaultSelections } from '../utils/preloadConfiguration/autosaveResume';
import { applyBusinessRulesForFabricSelection } from '../utils/businessRules';

const { ENABLE_AUTOSAVE } = FEATURE_TOGGLES;

type AutosaveContextProviderProps = {
  children: React.ReactNode;
  fabricList: typeFabricList;
  fabricSequence: number[];
  options: OptionConfigurationType;
  optionValues: typeOptionValue;
  defaultValues: DefaultValues;
  stylePresets: StylePresets;
  businessRules: BusinessRules;
  defaultSection: Section;
  fabricPreFilters: string;
  isInPreloadFlow: boolean;
  isLoginFlow: boolean;
};

export const AutosaveContextProvider = ({
  children,
  fabricList,
  fabricSequence,
  options,
  optionValues,
  defaultValues,
  stylePresets,
  businessRules,
  defaultSection,
  fabricPreFilters,
  isInPreloadFlow,
  isLoginFlow,
}: AutosaveContextProviderProps) => {
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousStateRef = useRef<AutosaveData | null>(null);
  const hasLoadInitializedRef = useRef<boolean>(false);
  const [autoSaveData, setAutoSaveData] = useState<AutosaveData | null>(null);
  const [isAutoSaveModalOpen, setIsAutoSaveModalOpen] = useState<boolean>(false);
  const isFirstRender = useFirstRender();
  const { selectedFabric, setSelectedFabric } = useFabricContext();
  const { section, setSection } = useNavigationContext();
  const { sizePassportSelection, setSizePassportSelection } = useSizePassportContext();
  const { isManualSelectionDone, setIsManualSelectionDone } = useGlobalContext();
  const { client, accountNr, product, rtwProductCodes, origin } = useUserContext();
  const { cacheBasePath } = useEnvironmentContext();
  const {
    hiddenOptionsAndValues,
    isAdditionalProductAdded,
    selectedBaseStyle,
    selectedMonogram,
    selectedOptions,
    setSelectedBaseStyle,
    setSelectedMonogram,
    setSelectedOptions,
    setHiddenOptionsAndValues,
    setIsAdditionalProductAdded,
  } = useOptionContext();
  const {
    setMissingOptionValuesPerConfig,
    setMissingMonogramValuesPerConfig,
    setMissingOptionsPerConfig,
    setMonogramsIdsWithMissingSelection,
    setShowMissingFabricModal,
    setShowMissingOptionsPanel,
  } = useMissingSelectionValuesContext();
  const isAutosaveFeatureEnabled = useFeatureIsOn(ENABLE_AUTOSAVE);

  const isInStore = client === Clients.inStore;
  const isCustomizeFlow = rtwProductCodes.length > 0;

  const getCurrentAutosaveState = (): AutosaveData => {
    return {
      hiddenOptionsAndValues: { ...hiddenOptionsAndValues },
      isAdditionalProductAdded: { ...isAdditionalProductAdded },
      isManualSelectionDone: isManualSelectionDone,
      section: section,
      selectedBaseStyle: { ...selectedBaseStyle },
      selectedFabric: selectedFabric,
      selectedMonogram: { ...selectedMonogram },
      selectedOptions: { ...selectedOptions },
      sizePassportSelection: { ...sizePassportSelection },
    };
  };

  const isAutosaveCacheEnabled = useEventCallback(() => {
    if (!isAutosaveFeatureEnabled) {
      return false;
    }

    return isInStore ? !!accountNr : client === Clients.onLine;
  });

  // login redirect always resumes regardless of those flags.
  const canOfferResume = () => {
    if (isLoginFlow) return true;
    if (isInPreloadFlow || fabricPreFilters || isCustomizeFlow) return false;
    return isInStore ? !!accountNr : client === Clients.onLine;
  };

  // Persist eligibility is intentionally broader than resume eligibility: the
  // customize flow must be saved so a later login redirect (isLoginFlow) can
  // resume it, even though it is not directly resumable. Every other
  // non-resumable flow (Reorder, FinishInStore, prefiltered sessions) stays
  // excluded so we don't create cache entries the resume path never reads back.
  const canPersistAutosave = () => canOfferResume() || isCustomizeFlow;

  const getAutosaveCacheKey = () => {
    return buildAutosaveCacheKey({
      accountNr: isInStore ? accountNr : undefined,
      product: product ?? 'Suit',
      client,
      origin,
      rtwProductCodes,
    });
  };

  const getAutosaveExpiryKey = () => {
    return `Exp-${getAutosaveCacheKey()}`;
  };

  const persistAutosaveState = (stateToSave: AutosaveData) => {
    if (isInStore) {
      fetch(cacheBasePath ?? '', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'x-cache-key': getAutosaveCacheKey(),
        },
        body: JSON.stringify(stateToSave),
      }).catch(error => {
        console.error('Error persisting autosave state to cache', error);
      });
      return;
    }

    try {
      localStorage.setItem(getAutosaveCacheKey(), JSON.stringify(stateToSave));
      localStorage.setItem(getAutosaveExpiryKey(), (Date.now() + storageTTL).toString());
    } catch {
      // Ignore errors
    }
  };

  const autosaveState = () => {
    if (!isInStore) {
      const stateToSave = getCurrentAutosaveState();
      if (!deepEqual(stateToSave, previousStateRef.current)) {
        previousStateRef.current = stateToSave;
        persistAutosaveState(stateToSave);
      }
      return;
    }

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(() => {
      const stateToSave: AutosaveData = getCurrentAutosaveState();

      // Only make the fetch request if the state has actually changed
      if (!deepEqual(stateToSave, previousStateRef.current)) {
        previousStateRef.current = stateToSave;
        persistAutosaveState(stateToSave);
      }
    }, 2000);
  };

  const getAutosaveStateFromCache = async () => {
    // Prevent multiple calls during initialization
    if (hasLoadInitializedRef.current || !canOfferResume()) {
      return;
    }

    hasLoadInitializedRef.current = true;

    if (!isInStore) {
      const raw = localStorage.getItem(getAutosaveCacheKey());
      if (!raw) {
        return;
      }

      const expiry = localStorage.getItem(getAutosaveExpiryKey());
      const isExpired = !expiry || Number.parseInt(expiry) < Date.now();
      if (isExpired) {
        removeAutosaveCache();
        return;
      }

      try {
        const data: AutosaveData = JSON.parse(raw);
        setAutoSaveData(data);
        setIsAutoSaveModalOpen(true);
      } catch (error) {
        console.error('Error parsing autosave state from localStorage', error);
        removeAutosaveCache();
      }
      return;
    }

    try {
      const response = await fetch(cacheBasePath ?? '', {
        method: 'GET',
        headers: {
          'x-cache-key': getAutosaveCacheKey(),
        },
      });
      if (response.ok && response.status === 200) {
        const data = await response.json();
        setAutoSaveData(data);
        setIsAutoSaveModalOpen(true);
      }
    } catch (error) {
      console.error('Error getting autosave state from cache', error);
    }
  };

  const closeAutoSaveModal = useEventCallback(() => {
    setIsAutoSaveModalOpen(false);
    removeAutosaveCache();
  });

  const resumeAutoSave = useEventCallback(() => {
    setIsAutoSaveModalOpen(false);

    if (!autoSaveData || !product) {
      return;
    }

    previousStateRef.current = autoSaveData;

    // Reuse the same validation pipeline as the server-side preload
    // (clone/reorder) flow so missing options / fabric / monograms are
    // detected and surfaced through the same panel/modal path.
    const {
      fabricPreselection,
      optionsPreselection,
      monogramsPreselection,
      stylePresetsPreselection,
      sizePassportPreselection,
      missingOptionValues,
      missingMonogramValues,
      missingOptionsPerConfig,
      monogramsIdsWithMissingSelection,
      showMissingFabricModal,
      showMissingOptionsPanel,
      sectionToStart,
      isAdditionalProductAdded: resolvedIsAdditionalProductAdded,
    } = getAutosaveResumeDefaultSelections(
      autoSaveData,
      defaultSection,
      product,
      fabricList,
      options,
      optionValues,
      defaultValues,
      stylePresets,
      origin,
    );

    // Fall back to the server-provided default fabric when available; otherwise
    // preserve the existing behavior of letting findFabricDefaultSelection
    // resolve to the first available fabric in the sequence when the cached
    // fabric is no longer available.
    const defaultFabricId = (defaultValues as DefaultValues & { fabricId?: string }).fabricId ?? '';
    const fabricToSelect = findFabricDefaultSelection(fabricList, fabricSequence, defaultFabricId, fabricPreselection);

    // Re-run business rules against the resolved fabric so hidden
    // options/values are recomputed from a known-good baseline rather than
    // trusting the cached hiddenOptionsAndValues snapshot.
    const { selectedOptions: ruledOptions, hiddenOptionsAndValues: ruledHidden } = applyBusinessRulesForFabricSelection(
      optionsPreselection,
      businessRules,
      getProductDefaultHiddenItemsState(product),
      optionValues,
      fabricToSelect.id,
      options,
      defaultValues,
      product,
      autoSaveData.isManualSelectionDone,
      stylePresetsPreselection,
      false, // isLogEnabled
      undefined, // prevFabricId
      true, // isPreloadFlow - treat resume as a preload-style recomputation
      false, // isPreloadSelectionApplied - force option-hide rules to re-derive
    );

    setSelectedFabric(fabricToSelect as Fabric);
    setHiddenOptionsAndValues(ruledHidden as HiddenOptionsAndValues);
    setIsAdditionalProductAdded(resolvedIsAdditionalProductAdded as Record<string, boolean>);
    setIsManualSelectionDone(autoSaveData.isManualSelectionDone);
    setSelectedBaseStyle(stylePresetsPreselection as Record<string, StylePreset>);
    setSelectedMonogram(monogramsPreselection as Monogram);
    setSelectedOptions(ruledOptions as OptionsSelection);
    setSizePassportSelection(sizePassportPreselection as SizePassportSelection);

    // Surface any missing values through the shared MissingSelectionValuesContext
    // so the existing missing-options panel and missing-fabric modal can react.
    setMissingOptionValuesPerConfig(missingOptionValues);
    setMissingMonogramValuesPerConfig(missingMonogramValues);
    setMissingOptionsPerConfig(missingOptionsPerConfig);
    setMonogramsIdsWithMissingSelection(monogramsIdsWithMissingSelection);
    setShowMissingFabricModal(showMissingFabricModal);
    setShowMissingOptionsPanel(showMissingOptionsPanel);

    // Land the user on the first section that has missing values (or the
    // cached section when nothing is missing).
    setSection(
      showMissingFabricModal || showMissingOptionsPanel ? sectionToStart : (autoSaveData.section ?? sectionToStart),
    );
  });

  const removeAutosaveCache = useEventCallback(() => {
    if (isInStore) {
      fetch(cacheBasePath ?? '', {
        method: 'DELETE',
        headers: {
          'x-cache-key': getAutosaveCacheKey(),
        },
      }).catch(error => {
        console.error('Error removing autosave state from cache', error);
      });
    } else {
      localStorage.removeItem(getAutosaveCacheKey());
      localStorage.removeItem(getAutosaveExpiryKey());
    }

    previousStateRef.current = getCurrentAutosaveState();
  });

  useEffect(() => {
    // Only persist sessions that are resumable now or later (canPersistAutosave
    // keeps the customize flow so a post-login redirect can resume it), so we no
    // longer create dangling cache entries for the non-resumable preload flows.
    // Skip while the resume modal is open so loading a saved session does not
    // overwrite it before the user chooses to resume or start fresh.
    if (isAutosaveCacheEnabled() && canPersistAutosave() && !isFirstRender && !isAutoSaveModalOpen) {
      autosaveState();
    }

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [
    hiddenOptionsAndValues,
    isAdditionalProductAdded,
    isManualSelectionDone,
    section,
    selectedBaseStyle,
    selectedFabric,
    selectedMonogram,
    selectedOptions,
    sizePassportSelection,
    isAutoSaveModalOpen,
    isFirstRender,
  ]);

  useEffect(() => {
    if (isAutosaveCacheEnabled() && !hasLoadInitializedRef.current) {
      void getAutosaveStateFromCache();
    }
  }, []);

  const contextValue = useMemo(() => {
    return {
      isAutoSaveModalOpen,
      isAutosaveCacheEnabled,
      closeAutoSaveModal,
      resumeAutoSave,
      removeAutosaveCache,
    };
  }, [isAutoSaveModalOpen, isAutosaveCacheEnabled, closeAutoSaveModal, resumeAutoSave, removeAutosaveCache]);

  return <AutosaveContext.Provider value={contextValue}>{children}</AutosaveContext.Provider>;
};
